import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, TextField, Typography, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { isTenantPortal, getTenantSlugFromHostname } from '../../utils/portalMode';
import api from '../../services/api';

interface TenantBranding {
  name: string;
  slug: string;
  agency_logo_url: string | null;
  timezone: string;
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isTenantPortal()) {
      const slug = getTenantSlugFromHostname();
      if (slug) {
        api.get(`/api/tenants/branding/${slug}`)
          .then(r => setBranding(r.data))
          .catch(() => {});
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = !isTenantPortal();
  const title = branding?.name || (isAdmin ? 'WatchCenter Station Alert' : 'WatchCenter Station Alert');
  const subtitle = isAdmin ? 'Tenant Management Portal' : 'Sign in to continue';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {branding?.agency_logo_url && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img
                src={branding.agency_logo_url}
                alt={`${branding.name} logo`}
                style={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
          <Typography variant="h5" align="center" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {subtitle}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <TextField label="Username" fullWidth margin="normal" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
            <TextField label="Password" type="password" fullWidth margin="normal" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" variant="contained" fullWidth size="large" sx={{ mt: 2 }} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
